import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { NotificationService } from '../../services/notification.service';
import { DynamicFormComponent } from '../../../shared/components/forms/dynamic-form/dynamic-form.component';
import {
  DynamicFormConfig,
  DynamicFormState,
  DynamicFormValue,
} from '../../../shared/components/forms/dynamic-form/models/dynamic-form.model';
import {
  DropMessageType,
  getValidationMessage,
  VALIDATION_MESSAGES,
} from '../../../shared/constant/validation-messages';

export type FormMode = 'create' | 'edit' | 'view' | 'comment';

export const DYNAMIC_FORM_HOST_TEMPLATE = `
  <div class="user-form-wrapper">
    <app-dynamic-form
      #dynamicForm
      [config]="formConfig"
      [initialValues]="initialValues"
      [disabled]="isLoading"
      [mode]="isDialogMode ? 'popup' : 'page'"
      (formSubmit)="handleFormSubmit($event)"
      (formCancel)="handleFormCancel()"
      (formChange)="handleFormChange($event)"
    >
    </app-dynamic-form>
  </div>
`;

export const DYNAMIC_FORM_HOST_STYLES = [
  `
    .user-form-wrapper {
      height: 100%;
      overflow-y: auto;
    }
  `,
];

@Directive()
export abstract class BaseFormComponent<TEntity = any, TPayload = any>
  implements OnInit, OnChanges, OnDestroy
{
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly notification = inject(NotificationService);
  protected readonly destroy$ = new Subject<void>();

  @ViewChild('dynamicForm') dynamicForm!: DynamicFormComponent;

  @Input() mode?: FormMode;
  @Input() readonly?: boolean;
  @Input() data?: any;

  @Output() formSubmit = new EventEmitter<DynamicFormValue>();
  @Output() formCancel = new EventEmitter<void>();

  dialogRef?: DialogRef;

  formConfig!: DynamicFormConfig;
  initialValues: DynamicFormValue = {};
  formMode: FormMode = 'create';
  isDialogMode = false;
  isLoading = false;
  currentFormState: DynamicFormState = {
    valid: false,
    dirty: false,
    touched: false,
    value: {},
    errors: null,
  };

  get isViewMode(): boolean {
    return this.formMode === 'view';
  }

  get isEditMode(): boolean {
    return this.formMode === 'edit';
  }

  get isCreateMode(): boolean {
    return this.formMode === 'create';
  }

  get canSave(): boolean {
    return !this.isViewMode && !this.isLoading && this.currentFormState.valid;
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.shouldReinitialize(changes)) {
      this.initializeForm();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected shouldReinitialize(changes: SimpleChanges): boolean {
    return ['mode', 'readonly', 'data', ...this.entityInputKeys()].some((key) => changes[key]);
  }

  protected entityInputKeys(): string[] {
    return [];
  }

  protected initializeForm(): void {
    this.isDialogMode = !!this.dialogRef;
    const entity = this.getEntityData();
    this.formMode = this.determineFormMode(entity);

    this.formConfig = this.buildFormConfig(this.formMode);
    this.formConfig.mode = this.isDialogMode ? 'popup' : 'page';
    this.formConfig.showActions = this.shouldShowActions();

    this.initialValues = entity
      ? this.mapEntityToFormValues(entity)
      : this.getDefaultInitialValues();

    this.cdr.markForCheck();
  }

  protected determineFormMode(entity: TEntity | null): FormMode {
    if (this.mode) return this.mode;
    if (this.readonly) return 'view';
    if (this.data?.mode) return this.data.mode;
    if (this.data?.readonly) return 'view';
    if (entity) return this.defaultModeWhenEntityPresent();
    return 'create';
  }

  protected defaultModeWhenEntityPresent(): FormMode {
    return 'edit';
  }

  protected shouldShowActions(): boolean {
    return !this.isDialogMode;
  }

  protected getDefaultInitialValues(): DynamicFormValue {
    return {};
  }

  protected abstract getEntityData(): TEntity | null;

  protected abstract buildFormConfig(mode: FormMode): DynamicFormConfig;

  protected abstract mapEntityToFormValues(entity: TEntity): DynamicFormValue;

  protected abstract preparePayload(formValue: DynamicFormValue): TPayload;

  protected abstract save(payload: TPayload): Observable<any>;

  protected abstract getEntityName(): string;

  handleFormChange(state: DynamicFormState): void {
    this.currentFormState = state;
    this.cdr.markForCheck();
  }

  handleFormSubmit(formValue: DynamicFormValue): void {
    if (this.isLoading || this.isViewMode) return;
    this.submitForm(formValue);
  }

  handleFormCancel(): void {
    this.formCancel.emit();
    this.dialogRef?.close({ success: false, action: 'cancel' });
  }

  protected submitForm(formValue: DynamicFormValue): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    const payload = this.preparePayload(formValue);

    this.save(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;

          if (response?.messageType === DropMessageType.Success) {
            this.formSubmit.emit(formValue);
            this.notification.success(this.getSuccessMessage(response, payload));
            this.dialogRef?.close({ success: true, action: 'save', data: response ?? payload });
            this.onSaveSuccess(response, payload);
          } else {
            this.notification.error(
              getValidationMessage(response?.message, this.getSaveErrorFallback())
            );
          }

          this.cdr.markForCheck();
        },
        error: (error: any) => {
          this.isLoading = false;
          this.notification.error(error?.error?.message ?? this.getSaveErrorFallback());
          this.cdr.markForCheck();
        },
      });
  }

  protected getSuccessMessage(response: any, _payload: TPayload): string {
    return getValidationMessage(response?.message, `${this.getEntityName()} saved successfully`);
  }

  protected getSaveErrorFallback(): string {
    return VALIDATION_MESSAGES.FAILED_TO_SAVE_DATA;
  }

  protected onSaveSuccess(_response: any, _payload: TPayload): void {}

  onSubmit(): void {
    if (this.dynamicForm && !this.isLoading) {
      this.dynamicForm.onSubmit();
    }
  }

  isFormDirty(): boolean {
    return this.currentFormState?.dirty ?? false;
  }

  isFormValid(): boolean {
    return this.currentFormState?.valid ?? false;
  }

  resetForm(): void {
    this.dynamicForm?.reset();
  }

  validateForm(): boolean {
    if (this.dynamicForm) {
      this.dynamicForm.validateAllFields();
      return this.dynamicForm.isValid();
    }
    return false;
  }

  getFormValue(): DynamicFormValue {
    return this.dynamicForm?.getValue() ?? {};
  }
}
